import { useEffect, useRef, useState } from 'react';

/** Ease-out cubic — fast start, gentle settle. */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Animate a number from 0 up to `target` over `durationMs`, returning the
 * current value each frame (eased with ease-out-cubic). Restarts whenever
 * `target` changes and cancels on unmount.
 *
 * Respects `prefers-reduced-motion`: reduced-motion users get the final value
 * immediately with no animation. The final frame always lands exactly on
 * `target` (no rounding drift). Purely numeric — callers format the result.
 */
export function useCountUp(target: number, durationMs = 900): number {
  // Reduced-motion / non-positive duration never animate — land on `target`
  // directly during render so the effect never has to sync state synchronously.
  const isStatic = prefersReducedMotion() || durationMs <= 0;
  // The target that `value` is currently animating toward is tracked *in* state
  // so a target change can be detected during render without reading a ref.
  const [state, setState] = useState(() => ({
    target,
    value: isStatic ? target : 0,
  }));
  const frameRef = useRef<number | null>(null);

  // Restart from 0 whenever the target changes. Adjusting state during render
  // (rather than in an effect) means the caller never paints a frame still
  // showing the previous target's value.
  // See https://react.dev/reference/react/useState#storing-information-from-previous-renders
  let value = state.value;
  if (state.target !== target) {
    value = isStatic ? target : 0;
    setState({ target, value });
  }

  useEffect(() => {
    if (isStatic) return;

    let start: number | null = null;

    const tick = (now: number): void => {
      // Seed the origin on the first animation frame — not synchronously in the
      // effect body — so the count-up always begins from 0 without a
      // cascading synchronous setState.
      start ??= now;
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / durationMs);
      // Land exactly on target on the last frame; ease the rest.
      setState({
        target,
        value: progress >= 1 ? target : target * easeOutCubic(progress),
      });
      if (progress < 1) frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [target, durationMs, isStatic]);

  return isStatic ? target : value;
}
